import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Move,
  Spinner,
  TabItem,
  TabList,
  Tabs,
  Text,
  useToast,
} from '@razorpay/blade/components';
import { FormProvider, useFormWithProvider } from 'forms';
import type {
  AttendanceSettingsFormState,
  AttendanceSettingsTabs,
} from 'components/Settings/AttendanceAndLeavesSettings/types';
import { routePaths } from 'components/Routes/data';
import { AttendanceDisabledPage } from './components/AttendanceDisabledPage';
import { DisableAttendanceModal } from './components/DisableAttendanceModal';
import { TimeAndAttendanceHeader } from './components/TimeAndAttendanceHeader';
import { AttendanceTab } from './AttendanceTab';
import { LeavesAndHolidaysTab } from './LeavesAndHolidaysTab';
import { PayRulesTab } from './PayRulesTab';

import { useAttendanceSettings } from './hooks/useAttendanceSettings';
import { useSaveAttendanceSettings } from './hooks/useSaveAttendanceSettings';
import { useToggleAttendance } from './hooks/useToggleAttendance';
import { attendanceSettingsValidation, getActiveTabFromPath } from './utils';
import { TAB_ROUTES, FIELD_TO_TAB } from './constants';

export const AttendanceAndLeavesSettings = () => {
  const { data: formState, isLoading, isError, refetch } = useAttendanceSettings();

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <Spinner accessibilityLabel="Loading attendance settings" size="large" />
      </Box>
    );
  }

  if (isError || !formState) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        gap="spacing.4">
        <Text color="feedback.text.negative.intense">Failed to load attendance settings.</Text>
        <Button variant="secondary" onClick={() => refetch()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <FormProvider<AttendanceSettingsFormState> initialState={formState}>
      <AttendanceSettingsInner />
    </FormProvider>
  );
};

const AttendanceSettingsInner = () => {
  const { formData, validate, initialData, setFieldValue, errors } =
    useFormWithProvider<AttendanceSettingsFormState>();
  const toast = useToast();
  const history = useHistory();
  const location = useLocation();
  const { mutate: save, isLoading: isSaving } = useSaveAttendanceSettings();
  const { mutate: toggleAttendance, isLoading: isToggling } = useToggleAttendance({
    onSuccess: ({ isAttendanceEnabled }) => {
      setFieldValue('isAttendanceEnabled', isAttendanceEnabled);
    },
  });

  const activeTab = getActiveTabFromPath(location.pathname);

  const setActiveTab = (tab: AttendanceSettingsTabs) => {
    history.push(TAB_ROUTES[tab]);
  };

  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);
  const prevTabRef = useRef<AttendanceSettingsTabs>(activeTab);

  useEffect(() => {
    if (prevTabRef.current !== activeTab) {
      prevTabRef.current = activeTab;
      const main = document.querySelector('main');
      if (main) main.scrollTop = 0;
    }
  }, [activeTab]);

  useEffect(() => {
    if (location.pathname === routePaths.orgSettings.timeAndAttendance.root) {
      history.replace(TAB_ROUTES.attendance);
    }
  }, [location.pathname, history]);

  const handleEnable = () => {
    toggleAttendance({ enabled: true, existingFormState: initialData });
  };

  const onDisableAttendance = () => {
    setIsDisableModalOpen(false);
    toggleAttendance({ enabled: false, existingFormState: initialData });
  };

  const handleSave = () => {
    if (!validate(attendanceSettingsValidation)) {
      const firstErrorField = Object.keys(attendanceSettingsValidation).find((field) => {
        const validator = attendanceSettingsValidation[field as keyof AttendanceSettingsFormState];
        return validator?.(formData as AttendanceSettingsFormState) != null;
      });

      if (firstErrorField) {
        const targetTab = FIELD_TO_TAB[firstErrorField];
        if (targetTab && targetTab !== activeTab) {
          setActiveTab(targetTab);
        }
      }

      toast.show({ content: 'Please fix the errors before saving.', color: 'negative' });
      return;
    }
    save(formData);
  };

  const handleBackClick = () => {
    if (activeTab === 'leaves') {
      setActiveTab('attendance');
      if (tabsRef.current) {
        tabsRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (activeTab === 'pay-rules') {
      setActiveTab('leaves');
      if (tabsRef.current) {
        tabsRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleNextClick = () => {
    if (activeTab === 'attendance') {
      setActiveTab('leaves');
      if (tabsRef.current) {
        tabsRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (activeTab === 'leaves') {
      setActiveTab('pay-rules');
      if (tabsRef.current) {
        tabsRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  if (!formData.isAttendanceEnabled) {
    if (isToggling) {
      return (
        <Box
          minHeight="80vh"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap="spacing.5"
          paddingX="spacing.8">
          <Spinner accessibilityLabel="Setting up Time & Attendance" size="large" />
          <Box display="flex" flexDirection="column" alignItems="center" gap="spacing.2">
            <Heading size="medium">Setting up Time &amp; Attendance…</Heading>
            <Text color="surface.text.gray.muted">This usually takes a moment.</Text>
          </Box>
        </Box>
      );
    }
    return <AttendanceDisabledPage onEnable={handleEnable} />;
  }

  return (
    <Box
      paddingX="spacing.8"
      paddingTop="spacing.8"
      minHeight="100%"
      display="flex"
      flexDirection="column">
      <Move motionTriggers={['mount']}>
        <Box>
          <TimeAndAttendanceHeader onDisableClick={() => setIsDisableModalOpen(true)} />
        </Box>
      </Move>

      <Move motionTriggers={['mount']} delay="2xquick">
        <Box
          ref={tabsRef}
          marginTop="spacing.6"
          position="sticky"
          top="spacing.0"
          zIndex={1}
          backgroundColor="surface.background.gray.moderate"
          elevation="lowRaised"
          marginX="-32px"
          paddingX="spacing.8"
          paddingTop="spacing.5">
          <Tabs
            variant="bordered"
            orientation="horizontal"
            size="medium"
            value={activeTab}
            onChange={(value: string) => setActiveTab(value as AttendanceSettingsTabs)}>
            <TabList>
              <TabItem value="attendance">Attendance</TabItem>
              <TabItem value="leaves">Leaves &amp; Holidays</TabItem>
              <TabItem value="pay-rules">Pay Rules</TabItem>
            </TabList>
          </Tabs>
        </Box>
      </Move>

      <Box marginTop="spacing.10" width="100%" maxWidth="800px" marginX="auto" flex="1">
        {activeTab === 'attendance' && (
          <AttendanceTab onNavigateToPayRules={() => setActiveTab('pay-rules')} />
        )}
        {activeTab === 'leaves' && <LeavesAndHolidaysTab />}
        {activeTab === 'pay-rules' && <PayRulesTab />}
      </Box>

      <Box
        position="sticky"
        bottom="0px"
        marginTop="spacing.10"
        marginX="-32px"
        paddingY="spacing.6"
        paddingX="spacing.8"
        backgroundColor="surface.background.gray.intense"
        borderTopWidth="thin"
        borderColor="surface.border.gray.subtle">
        <Box
          maxWidth="800px"
          marginX="auto"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap="spacing.6">
          <Box display="flex" alignItems="center">
            {activeTab !== 'attendance' && (
              <Button variant="tertiary" onClick={handleBackClick}>
                Back
              </Button>
            )}
          </Box>
          <Box display="flex" gap="spacing.4" alignItems="center">
            {activeTab !== 'pay-rules' && (
              <Button variant="secondary" onClick={handleNextClick} isLoading={isSaving}>
                Next
              </Button>
            )}
            <Button variant="primary" onClick={handleSave} isLoading={isSaving}>
              Save Changes
            </Button>
          </Box>
        </Box>
      </Box>

      <DisableAttendanceModal
        isOpen={isDisableModalOpen}
        onKeepEnabled={() => setIsDisableModalOpen(false)}
        onDisable={onDisableAttendance}
      />
    </Box>
  );
};
